import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface LocatorResponse {
  success: boolean;
  data?: Array<{ concatenatedSegments: string }>;
  message?: string;
}

interface UseLocatorLovProps {
  data: string[];
  isLoading: boolean;
  error: string | null;
  refetch: (
    orgId: string,
    subinventory: string,
    callback?: (data: string[]) => void
  ) => Promise<void>;
}

export const useLocatorLov = (
  toOrg?: string,
  toSubinventory?: string,
  executeApiOnload = true
): UseLocatorLovProps => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<string[]>([]);

  const fetchData = useCallback(
    async (
      orgId: string,
      subinventory: string,
      callback?: (data: string[]) => void
    ): Promise<void> => {
      // Validate input parameters
      if (!orgId || !subinventory) {
        const emptyData: string[] = [];
        callback?.(emptyData);
        setData(emptyData);
        return;
      }

      setIsLoading(true);
      setError(null);
      setData([]);

      try {
        const { data: responseData } = await apiClient.get<LocatorResponse>(
          API_CONFIG.ENDPOINTS.GET_LOCATOR_LOV,
          {
            params: {
              to_org: String(orgId || '').trim(),
              to_subinventory: String(subinventory || '').trim(),
            },
          }
        );

        const locatorList =
          responseData.success && responseData.data
            ? responseData.data.map((item) => item.concatenatedSegments)
            : [];

        setData(locatorList);
        callback?.(locatorList);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message;
        setError(errorMessage);
        console.error("Locator LOV Error:", errorMessage);
        callback?.([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Memoized refetch function
  const refetch = useCallback(
    async (
      orgId: string,
      subinventory: string,
      callback?: (data: string[]) => void
    ): Promise<void> => {
      await fetchData(orgId, subinventory, callback);
    },
    [fetchData]
  );

  useEffect(() => {
    if (executeApiOnload && toOrg && toSubinventory) {
      fetchData(toOrg, toSubinventory);
    }
  }, [executeApiOnload, toOrg, toSubinventory, fetchData]);

  return { data, isLoading, error, refetch };
};

